/**
 * What each of the centre's spreadsheets must carry, and how its rows become
 * what the API takes.
 *
 * Checked here only what can be told from the file alone — a missing column,
 * an empty required cell, a date that is not one. Everything that needs the
 * centre (is this mentor ours, is that course ours, is this address taken) is
 * the API's answer, per row, after the import.
 */

import { CsvTable, normalizeHeader, parseCsvDate } from "../../../shared/domain/csv";
import { cell, missingColumns } from "../../../shared/domain/csvImport";
import type { CsvColumn, CsvImport, CsvRowError } from "../../../shared/domain/csvImport";

export type { CsvColumn, CsvImport, CsvRowError };
import type { CoursePhase, CourseInput, PatientCustomField, StudentInput } from "../../center";
import { whatsappNumberForForm } from "../../../shared/domain/whatsappNumber";

/** A centre's own field, as a column of its patient or student file. */
export interface CustomFieldColumn extends CsvColumn {
    field: PatientCustomField;
}

export const MENTOR_COLUMNS: CsvColumn[] = [
    { key: "email", aliases: ["correo", "e_mail", "correo_electronico", "mail"], required: true, example: "mentor@escuela.es" },
];

export const COURSE_COLUMNS: CsvColumn[] = [
    { key: "nombre_publico", aliases: ["public_name", "nombre", "name"], required: true, example: "Formación en Terapia Gestalt" },
    { key: "nombre_interno", aliases: ["internal_name"], required: true, example: "GES-2026-FDS" },
    { key: "modulo_nombre", aliases: ["fase_nombre", "phase_name", "module_name", "modulo", "fase", "module"], required: true, example: "Fundamentos" },
    { key: "modulo_descripcion", aliases: ["fase_descripcion", "phase_description", "module_description", "descripcion"], required: false, example: "Bases teóricas del enfoque" },
    { key: "modulo_inicio", aliases: ["fase_inicio", "phase_start", "module_start", "inicio", "fecha_inicio"], required: false, example: "2026-10-01" },
    { key: "modulo_fin", aliases: ["fase_fin", "phase_end", "module_end", "fin", "fecha_fin"], required: false, example: "2027-01-31" },
    { key: "modulo_temas", aliases: ["fase_temas", "phase_topics", "module_topics", "temas", "topics"], required: false, example: "Awareness|Contacto|Ciclo de la experiencia" },
    { key: "modulo_resumen", aliases: ["fase_resumen", "phase_summary", "module_summary", "resumen", "summary"], required: false, example: "Primer trimestre" },
];

export const STUDENT_COLUMNS: CsvColumn[] = [
    { key: "nombre", aliases: ["first_name", "name"], required: true, example: "Clara" },
    { key: "apellidos", aliases: ["last_name", "surname", "apellido"], required: true, example: "Ruiz Soler" },
    { key: "email", aliases: ["correo", "e_mail", "correo_electronico"], required: true, example: "clara@example.com" },
    { key: "telefono", aliases: ["phone", "movil", "telefono_movil"], required: true, example: "+34600111222" },
    { key: "curso", aliases: ["nombre_interno", "curso_interno", "course", "course_name"], required: true, example: "GES-2026-FDS" },
    { key: "email_mentor", aliases: ["mentor_email", "correo_mentor"], required: true, example: "mentor@escuela.es" },
    { key: "anio_academico", aliases: ["academic_year", "ano_academico", "curso_academico"], required: false, example: "2026" },
    { key: "grupo", aliases: ["group"], required: false, example: "" },
    { key: "pronombres", aliases: ["pronouns"], required: false, example: "ella" },
    { key: "fecha_nacimiento", aliases: ["date_of_birth", "nacimiento", "birth_date"], required: false, example: "1995-04-12" },
    { key: "sexo", aliases: ["sex"], required: false, example: "mujer" },
    { key: "calle", aliases: ["street", "direccion"], required: false, example: "Calle Mayor 1" },
    { key: "ciudad", aliases: ["city"], required: false, example: "Bilbao" },
    { key: "provincia", aliases: ["state", "region"], required: false, example: "Bizkaia" },
    { key: "codigo_postal", aliases: ["postal_code", "cp", "zip"], required: false, example: "48001" },
    { key: "pais", aliases: ["country"], required: false, example: "ES" },
    { key: "zona_horaria", aliases: ["timezone"], required: false, example: "Europe/Madrid" },
    { key: "whatsapp", aliases: ["whatsapp_phone", "telefono_whatsapp", "movil_whatsapp"], required: false, example: "+34600111222" },
];

/**
 * The patient's intake profile ("ficha"), as optional columns of a therapy
 * centre's file. A school's students have none.
 */
export const INTAKE_COLUMNS: (CsvColumn & { intakeField: string })[] = [
    { key: "edad", aliases: ["age"], required: false, example: "34", intakeField: "age" },
    { key: "profesion", aliases: ["occupation", "ocupacion"], required: false, example: "Docente", intakeField: "occupation" },
    { key: "con_quien_vive", aliases: ["lives_with", "convivencia"], required: false, example: "Con su pareja", intakeField: "lives_with" },
    { key: "hijos", aliases: ["children", "numero_hijos", "num_hijos"], required: false, example: "2", intakeField: "children" },
    { key: "nucleo_familiar", aliases: ["family_nucleus", "familia"], required: false, example: "Padres y un hermano", intakeField: "family_nucleus" },
    { key: "tema_nuclear", aliases: ["core_issue", "motivo_consulta", "motivo"], required: false, example: "Ansiedad en el trabajo", intakeField: "core_issue" },
    { key: "peticion", aliases: ["request", "demanda", "peticion_demanda"], required: false, example: "Aprender a gestionar el estrés", intakeField: "request" },
];

/** "None" written as a word: the profile stores it as no children. */
const NO_CHILDREN = new Set(["no", "n", "ninguno", "ninguna", "none"]);

/** The profile's own limits, as the server holds them; it checks again. */
const INTAKE_TEXT_MAX = 500;
const INTAKE_AGE_MAX = 120;
const INTAKE_CHILDREN_MAX = 20;

function wholeNumber(raw: string, highest: number): string | undefined {
    const count = Number(raw);
    return raw !== "" && Number.isInteger(count) && count >= 0 && count <= highest ? String(count) : undefined;
}

/** A profile cell as the API takes it; undefined when it cannot be one. */
function intakeValue(intakeField: string, raw: string): string | undefined {
    if (intakeField === "age") return wholeNumber(raw, INTAKE_AGE_MAX);
    if (intakeField === "children") {
        return wholeNumber(NO_CHILDREN.has(raw.toLowerCase()) ? "0" : raw, INTAKE_CHILDREN_MAX);
    }
    return raw.length <= INTAKE_TEXT_MAX ? raw : undefined;
}

/** A therapy centre's patients: the same record, no course, and couples as well as groups. */
export const PATIENT_COLUMNS: CsvColumn[] = [
    ...STUDENT_COLUMNS.filter((column) => !["curso", "anio_academico", "email_mentor"].includes(column.key)).slice(0, 4),
    { key: "email_terapeuta", aliases: ["therapist_email", "email_mentor", "correo_terapeuta"], required: true, example: "terapeuta@centro.es" },
    { key: "grupo", aliases: ["group"], required: false, example: "" },
    { key: "pareja", aliases: ["couple"], required: false, example: "" },
    ...STUDENT_COLUMNS.filter((column) =>
        ["pronombres", "fecha_nacimiento", "sexo", "calle", "ciudad", "provincia", "codigo_postal", "pais", "zona_horaria", "whatsapp"].includes(column.key)
    ),
    ...INTAKE_COLUMNS,
];

const CUSTOM_FIELD_EXAMPLES: Record<PatientCustomField["field_type"], string> = {
    text: "",
    number: "1",
    date: "2026-01-15",
    select: "",
};

/**
 * One column per custom field of the centre, in the centre's order, headed by
 * its label as a header reads (`Nº expediente` → `n_expediente`). A label
 * that reads as one of the file's own columns, or as another field's, is told
 * apart with a `campo_` prefix and, if still needed, a number.
 */
export function customFieldColumns(fields: PatientCustomField[], fixed: CsvColumn[]): CustomFieldColumn[] {
    const used = new Set(fixed.flatMap((column) => [column.key, ...column.aliases]));
    return [...fields]
        .sort((a, b) => a.position - b.position)
        .map((field, index) => {
            const base = normalizeHeader(field.label) || `campo_${index + 1}`;
            let key = used.has(base) ? `campo_${base}` : base;
            for (let n = 2; used.has(key); n += 1) key = `campo_${base}_${n}`;
            used.add(key);
            return {
                key,
                aliases: [],
                required: field.required,
                example: field.field_type === "select" ? (field.options[0] ?? "") : CUSTOM_FIELD_EXAMPLES[field.field_type],
                description: field.label,
                field,
            };
        });
}

/** The longest value the server keeps for a custom field. */
const CUSTOM_VALUE_MAX = 200;

/** A cell of a custom field as the API takes it; undefined when it cannot be one. */
function customValue(field: PatientCustomField, raw: string): string | null | undefined {
    if (!raw) return null;
    if (raw.length > CUSTOM_VALUE_MAX) return undefined;
    switch (field.field_type) {
        case "date":
            return parseCsvDate(raw) ?? undefined;
        case "number": {
            // A spreadsheet in Spanish writes a decimal comma.
            const number = raw.replace(",", ".");
            return number.trim() !== "" && Number.isFinite(Number(number)) ? number : undefined;
        }
        case "select":
            return field.options.find((option) => option.toLowerCase() === raw.toLowerCase());
        default:
            return raw;
    }
}

/** What the patient form stores for each answer a spreadsheet may give. */
const SEX_VALUES: Record<string, string> = {
    mujer: "female", femenino: "female", female: "female", f: "female",
    hombre: "male", masculino: "male", male: "male", m: "male",
    otro: "other", other: "other",
};
const PRONOUN_VALUES: Record<string, string> = {
    "ella": "she/her", "she/her": "she/her",
    "el": "he/him", "él": "he/him", "he/him": "he/him",
    "elle": "they/them", "they/them": "they/them",
};

/** The value of a column in a record, under its key or any of its aliases. */


function byKey(columns: CsvColumn[]): Record<string, CsvColumn> {
    return Object.fromEntries(columns.map((column) => [column.key, column]));
}

/** Required cells empty in this record, as errors. */
function emptyRequired(values: Record<string, string>, columns: CsvColumn[], line: number): CsvRowError[] {
    return columns
        .filter((column) => column.required && !cell(values, column))
        .map((column) => ({ line, code: "missing_value" as const, column: column.key }));
}

export function readMentors(table: CsvTable): CsvImport<string> {
    const missing = missingColumns(table, MENTOR_COLUMNS);
    if (missing.length) return { items: [], errors: [], missingColumns: missing };
    const [email] = MENTOR_COLUMNS;
    const items: CsvImport<string>["items"] = [];
    const errors: CsvRowError[] = [];
    for (const record of table.records) {
        const value = cell(record.values, email);
        if (value) items.push({ value, lines: [record.line] });
        else errors.push({ line: record.line, code: "missing_value", column: email.key });
    }
    return { items, errors, missingColumns: [] };
}

/** "2025", "2025/26" or "2025-2026" → 2025; null when empty; NaN when not a year. */
export function parseAcademicYear(raw: string): number | null {
    const value = raw.trim();
    if (!value) return null;
    const match = /^(\d{4})(?:\s*[/-]\s*\d{2,4})?$/.exec(value);
    return match ? Number(match[1]) : Number.NaN;
}

export function readStudents(table: CsvTable, customColumns: CustomFieldColumn[] = []): CsvImport<StudentInput> {
    return readPeople(table, STUDENT_COLUMNS, customColumns);
}

export function readPatients(table: CsvTable, customColumns: CustomFieldColumn[] = []): CsvImport<StudentInput> {
    return readPeople(table, PATIENT_COLUMNS, customColumns);
}

/**
 * Rows of people, as either file names them: a school's with a course, a
 * therapy centre's without — and the centre's own fields, when it has any.
 */
function readPeople(
    table: CsvTable,
    fixedColumns: CsvColumn[],
    customColumns: CustomFieldColumn[]
): CsvImport<StudentInput> {
    const columns = [...fixedColumns, ...customColumns];
    const missing = missingColumns(table, columns);
    if (missing.length) return { items: [], errors: [], missingColumns: missing };
    const col = byKey(columns);
    const items: CsvImport<StudentInput>["items"] = [];
    const errors: CsvRowError[] = [];

    for (const { line, values } of table.records) {
        // A column this file kind does not have reads as empty.
        const value = (key: string) => (col[key] ? cell(values, col[key]) : "");
        const rowErrors = emptyRequired(values, columns, line);
        const year = parseAcademicYear(value("anio_academico"));
        if (year !== null && Number.isNaN(year)) {
            rowErrors.push({ line, code: "invalid_year", column: "anio_academico" });
        }
        const rawBirth = value("fecha_nacimiento");
        const birth = rawBirth ? parseCsvDate(rawBirth) : null;
        if (rawBirth && !birth) rowErrors.push({ line, code: "invalid_date", column: "fecha_nacimiento" });
        const rawSex = value("sexo").toLowerCase();
        const sex = rawSex ? SEX_VALUES[rawSex] : null;
        if (rawSex && !sex) rowErrors.push({ line, code: "invalid_value", column: "sexo" });
        const rawPronouns = value("pronombres");
        const pronouns = rawPronouns ? (PRONOUN_VALUES[rawPronouns.toLowerCase()] ?? rawPronouns) : null;
        const country = value("pais").toUpperCase() || "ES";
        // A national number is completed with the row's country, as in the forms.
        const whatsapp = whatsappNumberForForm(value("whatsapp"), country);
        if (whatsapp === undefined) rowErrors.push({ line, code: "invalid_value", column: "whatsapp" });
        const customValues: Record<string, string | null> = {};
        for (const column of customColumns) {
            const read = customValue(column.field, cell(values, column));
            if (read === undefined) {
                const code = column.field.field_type === "date" ? "invalid_date" : "invalid_value";
                rowErrors.push({ line, code, column: column.key });
            } else {
                customValues[column.field.id] = read;
            }
        }
        const intakeValues = readIntake(values, fixedColumns, line, rowErrors);
        if (rowErrors.length) {
            errors.push(...rowErrors);
            continue;
        }
        const person: StudentInput = {
            first_name: value("nombre"),
            last_name: value("apellidos"),
            contact_email: value("email"),
            contact_phone: value("telefono"),
            mentor_email: value("email_mentor") || value("email_terapeuta"),
            group: value("grupo") || null,
            pronouns,
            date_of_birth: birth,
            sex,
            street: value("calle") || null,
            city: value("ciudad") || null,
            state: value("provincia") || null,
            postal_code: value("codigo_postal") || null,
            country,
            timezone: value("zona_horaria") || null,
            whatsapp_phone: whatsapp ?? null,
        };
        if (col.curso) {
            person.course_name = value("curso");
            person.academic_year = year;
        }
        if (col.pareja) person.couple = value("pareja") || null;
        if (customColumns.length) person.custom_values = customValues;
        if (Object.keys(intakeValues).length) person.intake_values = intakeValues;
        items.push({ lines: [line], value: person });
    }
    return { items, errors, missingColumns: [] };
}

/** The row's intake values, when the file kind has the columns; a refused cell is added to `rowErrors`. */
function readIntake(
    values: Record<string, string>,
    fixedColumns: CsvColumn[],
    line: number,
    rowErrors: CsvRowError[]
): Record<string, string> {
    const intake: Record<string, string> = {};
    for (const column of INTAKE_COLUMNS.filter((c) => fixedColumns.includes(c))) {
        const raw = cell(values, column).trim();
        if (!raw) continue;
        const value = intakeValue(column.intakeField, raw);
        if (value === undefined) rowErrors.push({ line, code: "invalid_value", column: column.key });
        else intake[column.intakeField] = value;
    }
    return intake;
}

/**
 * One course per internal name, its modules in the order of their rows.
 *
 * A course runs over several rows, one per module, repeating its names on
 * each. Rows that name the same internal name with a different public name
 * are a mistake in the file, not two courses: the later ones are refused.
 */
export function readCourses(table: CsvTable): CsvImport<CourseInput> {
    const missing = missingColumns(table, COURSE_COLUMNS);
    if (missing.length) return { items: [], errors: [], missingColumns: missing };
    const col = byKey(COURSE_COLUMNS);
    const courses = new Map<string, { value: CourseInput & { phases: CoursePhase[] }; lines: number[] }>();
    const errors: CsvRowError[] = [];

    for (const { line, values } of table.records) {
        const rowErrors = emptyRequired(values, COURSE_COLUMNS, line);
        const dates: Record<string, string | null> = {};
        for (const column of [col.modulo_inicio, col.modulo_fin]) {
            const raw = cell(values, column);
            dates[column.key] = raw ? parseCsvDate(raw) : null;
            if (raw && !dates[column.key]) rowErrors.push({ line, code: "invalid_date", column: column.key });
        }
        const publicName = cell(values, col.nombre_publico);
        const internalName = cell(values, col.nombre_interno);
        const key = internalName.toLowerCase();
        const existing = courses.get(key);
        if (existing && existing.value.name !== publicName && publicName) {
            rowErrors.push({ line, code: "course_mismatch", column: col.nombre_publico.key });
        }
        if (rowErrors.length) {
            errors.push(...rowErrors);
            continue;
        }

        const phase: CoursePhase = {
            name: cell(values, col.modulo_nombre),
            description: cell(values, col.modulo_descripcion) || null,
            starts_on: dates[col.modulo_inicio.key],
            ends_on: dates[col.modulo_fin.key],
            topics: cell(values, col.modulo_temas)
                .split("|")
                .map((topic) => topic.trim())
                .filter(Boolean),
            summary: cell(values, col.modulo_resumen) || null,
        };
        if (existing) {
            existing.value.phases.push(phase);
            existing.lines.push(line);
        } else {
            courses.set(key, {
                value: { name: publicName, internal_name: internalName, phases: [phase] },
                lines: [line],
            });
        }
    }
    return { items: [...courses.values()], errors, missingColumns: [] };
}
